/* eslint-disable no-nested-ternary */
import { zodResolver } from '@hookform/resolvers/zod';
import equal from 'fast-deep-equal';
import { Save } from 'lucide-react';
import { ReactNode, useEffect, useState } from 'react';
import { type FieldPath, FieldValues, useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import { GroupedCombobox } from '@/components/ui/grouped-combobox';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  useFirmDialpadNumbersListQuery,
  useSetFirmAddressMutation,
} from '@/services/api/firmsService';
import {
  CMSSystem,
  CreateFirmPayload,
  Firm,
  FirmStatus,
  FirmType,
  LeadSystem,
  MedRecsBrandingMode,
  TimeZone,
  UpdateFirmPayload,
} from '@/services/types/firms-types';
import { useListFrontChannels } from '@/services/useFrontChannels';

import { FrontChannelSelect } from './components/FrontChannelSelect';
import {
  FirmFieldConfigV2,
  FirmFieldRow,
  FirmLayout,
  getFirmFieldsV2,
} from './layout/FirmLayout';
import { ParalegalSelect } from './ParalegalSelect';

// Address sub-schema
const addressSchema = z.object({
  line1: z.string().optional().nullable(),
  line2: z.string().optional().nullable(),
  line3: z.string().optional().nullable(),
  city: z.string().optional().nullable(),
  state: z.string().optional().nullable(),
  postcode: z.string().optional().nullable(),
  country: z.string().optional().nullable(),
});

// Zod schema for form validation
const firmFormSchema = z.object({
  name: z.string().min(1, 'Firm name is required'),
  full_name: z.string().optional().nullable(),
  email: z
    .string()
    .email('Invalid email format')
    .optional()
    .nullable()
    .or(z.literal('')),
  adobe_sign_email: z
    .string()
    .email('Invalid email format')
    .optional()
    .nullable()
    .or(z.literal('')),
  filevine_org_id: z.coerce.number().int().optional().nullable(),
  status: z.nativeEnum(FirmStatus).optional().nullable(),
  type: z.nativeEnum(FirmType).optional().nullable(),
  medrecs_branding_mode: z.nativeEnum(MedRecsBrandingMode).optional().nullable(),
  time_zone: z.nativeEnum(TimeZone).optional().nullable(),
  primary_state: z.string().optional().nullable(),
  cms_system: z.nativeEnum(CMSSystem).optional().nullable(),
  cms_link: z
    .string()
    .optional()
    .nullable()
    .or(z.literal(''))
    .transform((val) => {
      if (!val || val === '') return null;
      // Auto-prepend https:// if no protocol is present
      if (!/^https?:\/\//i.test(val)) {
        return `https://${val}`;
      }
      return val;
    }),
  lead_system: z.nativeEnum(LeadSystem).optional().nullable(),
  lead_system_link: z
    .string()
    .optional()
    .nullable()
    .or(z.literal(''))
    .transform((val) => {
      if (!val || val === '') return null;
      // Auto-prepend https:// if no protocol is present
      if (!/^https?:\/\//i.test(val)) {
        return `https://${val}`;
      }
      return val;
    }),
  billing_config: z
    .object({
      contracted_case_count: z
        .preprocess(
          (v) => (v === '' || v === undefined ? null : v),
          z.coerce.number().int().min(0).nullable(),
        )
        .optional()
        .nullable(),
    })
    .optional()
    .nullable(),
  case_preferences: z.string().optional().nullable(),
  commentary: z.string().optional().nullable(),
  errors: z.string().optional().nullable(),
  plan_to_derisk: z.string().optional().nullable(),
  managing_paralegal_id: z.string().min(1, 'Managing paralegal is required'),
  front_channel_front_id: z.string().optional().nullable(),
  // SMS sender number (Dialpad phone number in E.164 format)
  sms_sender_number: z.string().optional().nullable(),
  // Fax number
  fax: z.string().optional().nullable(),
  // EIN (Employer Identification Number) in XX-XXXXXXX format
  ein: z
    .string()
    .optional()
    .nullable()
    .or(z.literal(''))
    .refine(
      (val) => {
        if (!val || val === '') return true;
        // EIN format: XX-XXXXXXX (2 digits, dash, 7 digits)
        return /^\d{2}-\d{7}$/.test(val);
      },
      { message: 'EIN must be in XX-XXXXXXX format (e.g., 12-3456789)' },
    ),
  // Address fields
  address: addressSchema.optional().nullable(),
});

type FirmFormValues = z.infer<typeof firmFormSchema>;

export interface EditFirmProps {
  initialValues?: Partial<Firm>;
  onSubmit?: (values: CreateFirmPayload | UpdateFirmPayload) => Promise<void>;
  onCancel?: () => void;
  isSubmitting?: boolean;
  mode: 'create' | 'edit';
  inline?: boolean;
  firm?: Firm;
  header?: ReactNode;
}

// Helper to get default values from the firm
const getDefaultValues = (data: Partial<Firm> = {}): FirmFormValues => ({
  name: data.name || '',
  full_name: data.full_name ?? null,
  email: data.email ?? null,
  adobe_sign_email: data.adobe_sign_email ?? null,
  filevine_org_id: data.filevine_org_id ?? null,
  status: data.status ?? null,
  type: data.type ?? null,
  medrecs_branding_mode: data.medrecs_branding_mode ?? null,
  time_zone: data.time_zone ?? null,
  primary_state: data.primary_state ?? null,
  cms_system: data.cms_system ?? null,
  cms_link: data.cms_link ?? null,
  lead_system: data.lead_system ?? null,
  lead_system_link: data.lead_system_link ?? null,
  billing_config: data.billing_config ?? { contracted_case_count: null },
  case_preferences: data.case_preferences ?? null,
  commentary: data.commentary ?? null,
  errors: data.errors ?? null,
  plan_to_derisk: data.plan_to_derisk ?? null,
  managing_paralegal_id: data.managing_paralegal_id ?? '',
  front_channel_front_id: data.front_channel?.front_id ?? null,
  sms_sender_number: data.sms_sender_number ?? null,
  fax: data.fax ?? null,
  ein: data.ein ?? null,
  address: data.address
    ? {
        line1: data.address.line1 ?? null,
        line2: data.address.line2 ?? null,
        line3: data.address.line3 ?? null,
        city: data.address.city ?? null,
        state: data.address.state ?? null,
        postcode: data.address.postcode ?? null,
        country: 'US',
      }
    : null,
});

export const EditFirm: React.FC<EditFirmProps> = ({
  initialValues = {},
  onSubmit,
  onCancel,
  isSubmitting = false,
  mode,
  inline = false,
  firm,
  header,
}) => {
  const [isLoading, setIsLoading] = useState(isSubmitting);
  const [hasFormChanges, setHasFormChanges] = useState(mode === 'create');
  const { channels, isLoading: isLoadingChannels } = useListFrontChannels();
  const { data: dialpadNumbersData, isLoading: isLoadingDialpadNumbers } =
    useFirmDialpadNumbersListQuery(
      { firmPk: firm?.id ?? '' },
      { skip: !firm?.id },
    );
  const [setFirmAddress] = useSetFirmAddressMutation();

  // Use memoized default values for consistent comparison
  const defaultValues = getDefaultValues(mode === 'edit' ? initialValues : {});

  const form = useForm<FirmFormValues>({
    resolver: zodResolver(firmFormSchema),
    defaultValues,
  });

  // Update loading state when prop changes
  useEffect(() => {
    setIsLoading(isSubmitting);
  }, [isSubmitting]);

  // Reset form when initialValues change (only in edit mode)
  useEffect(() => {
    if (mode === 'edit' && initialValues) {
      form.reset(getDefaultValues(initialValues));
    }
  }, [initialValues, form, mode]);

  // Watch all form values to detect changes
  const formValues = form.watch();

  // Determine if there are meaningful changes
  useEffect(() => {
    if (mode === 'create') {
      // For create mode, enable the button once all required fields have values
      setHasFormChanges(
        Boolean(formValues.name && formValues.managing_paralegal_id),
      );
    } else {
      const defaults = getDefaultValues(initialValues);
      setHasFormChanges(!equal(defaults, formValues));
    }
  }, [formValues, initialValues, mode]);

  // Compare current values against initial values using deep equality
  const getChangedFields = <T extends FieldValues>(
    currentValues: T,
    originalValues: T,
  ): Partial<T> =>
    (Object.keys(currentValues) as (keyof T)[]).reduce((acc, key) => {
      if (!equal(currentValues[key], originalValues[key])) {
        return { ...acc, [key]: currentValues[key] };
      }
      return acc;
    }, {} as Partial<T>);

  // Check if address fields have been modified using deep comparison
  const hasAddressChanged = (
    currentAddress: FirmFormValues['address'],
    initialAddress: FirmFormValues['address'],
  ): boolean => {
    return !equal(currentAddress, initialAddress);
  };

  const handleSubmit = async (values: FirmFormValues) => {
    if (!onSubmit) return;

    try {
      setIsLoading(true);
      const defaults = getDefaultValues(initialValues);

      // Get changed firm fields, excluding address (handled separately)
      const { address: _addressValues, ...otherValues } = values;
      const { address: _addressDefaults, ...otherDefaults } = defaults;

      const changedFirmFields = getChangedFields(
        otherValues,
        otherDefaults,
      ) as Omit<UpdateFirmPayload, 'id'>;

      if (mode === 'edit' && firm?.id) {
        const hasFirmChanges = Object.keys(changedFirmFields).length > 0;
        const addressChanges = hasAddressChanged(
          values.address,
          _addressDefaults,
        );

        // Update firm fields (excluding address)
        if (hasFirmChanges) {
          const updatePayload: UpdateFirmPayload = {
            id: firm.id,
            ...changedFirmFields,
          };
          await onSubmit(updatePayload);
        }

        // Update address via dedicated endpoint if address fields changed
        if (addressChanges && values.address) {
          await setFirmAddress({
            id: firm.id,
            patchedCreateAddressSchema: {
              line1: values.address.line1,
              line2: values.address.line2,
              line3: values.address.line3,
              city: values.address.city,
              state: values.address.state,
              postcode: values.address.postcode,
              country: 'US',
            },
          });

          // If only address changed (no firm fields), still call onSubmit to trigger navigation
          if (!hasFirmChanges) {
            await onSubmit({ id: firm.id });
          }
        }

        // Reset the form to the submitted values in edit mode
        form.reset(values);
      } else {
        // For create mode, just submit firm data (address will be added after firm creation)
        await onSubmit(changedFirmFields as CreateFirmPayload);
        // Reset the form to default empty values in create mode
        form.reset(getDefaultValues({}));
      }

      setHasFormChanges(false);
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Form actions footer
  const formActions = (
    <div className="flex justify-end space-x-4">
      <Button
        type="button"
        variant="outline"
        onClick={onCancel}
        disabled={isLoading}
      >
        Cancel
      </Button>
      <Button
        type="submit"
        disabled={isLoading || !hasFormChanges}
        className="gap-1"
      >
        <Save className="h-4 w-4" />
        {isLoading
          ? 'Saving...'
          : mode === 'edit'
            ? 'Update Firm'
            : 'Create Firm'}
      </Button>
    </div>
  );

  // Render form fields
  const renderFormField = (field: FirmFieldConfigV2) => (
    <FormField
      key={field.name}
      control={form.control}
      name={field.name as keyof FirmFormValues}
      render={({ field: fieldProps }) => (
        <FormItem>
          <FormControl>
            {field.type === 'select' && field.options ? (
              <Select
                onValueChange={fieldProps.onChange}
                value={
                  fieldProps.value !== null && fieldProps.value !== undefined
                    ? String(fieldProps.value)
                    : undefined
                }
                disabled={fieldProps.disabled}
              >
                <SelectTrigger>
                  <SelectValue placeholder={field.placeholder} />
                </SelectTrigger>
                <SelectContent>
                  {field.options.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : field.type === 'textarea' ? (
              <Textarea
                placeholder={field.placeholder}
                {...fieldProps}
                value={String(fieldProps.value ?? '')}
                rows={4}
                className="resize-y"
              />
            ) : (
              <Input
                type={field.type === 'url' ? 'url' : field.type}
                placeholder={field.placeholder}
                {...fieldProps}
                value={String(fieldProps.value ?? '')}
              />
            )}
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <FirmLayout
          header={header}
          footer={
            inline ? (
              <div className="pt-4 border-t">{formActions}</div>
            ) : (
              formActions
            )
          }
          inline={inline}
        >
          {getFirmFieldsV2().map((field) => {
            if (field.transformValue) {
              return null;
            }
            return (
              <FirmFieldRow
                key={field.name}
                label={field.label}
                required={field.required}
              >
                {renderFormField(field)}
              </FirmFieldRow>
            );
          })}

          <FirmFieldRow label="Contracted Cases / Month" required={false}>
            <FormField
              control={form.control}
              name={'billing_config.contracted_case_count' as const satisfies FieldPath<FirmFormValues>}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="Enter contracted monthly case target"
                      {...field}
                      value={field.value ?? ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FirmFieldRow>
          {/* Managing Paralegal Field */}
          <FirmFieldRow label="Managing Paralegal" required>
            <FormField
              control={form.control}
              name="managing_paralegal_id"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ParalegalSelect
                      value={field.value}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FirmFieldRow>
          {/* Front Channel selection (searchable) with safeguards */}
          <FrontChannelSelect
            control={form.control}
            channels={channels || []}
            isLoading={isLoadingChannels}
            currentFirmId={firm?.id}
          />

          {/* Fax Number */}
          <FirmFieldRow label="Fax Number" required={false}>
            <FormField
              control={form.control}
              name="fax"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      type="tel"
                      placeholder="Firm fax number"
                      {...field}
                      value={field.value ?? ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FirmFieldRow>

          {/* EIN (Employer Identification Number) */}
          <FirmFieldRow label="EIN" required={false}>
            <FormField
              control={form.control}
              name="ein"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      type="text"
                      placeholder="XX-XXXXXXX"
                      {...field}
                      value={field.value ?? ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FirmFieldRow>

          {/* SMS Sender Number */}
          <FirmFieldRow label="SMS Sender Number" required={false}>
            <FormField
              control={form.control}
              name="sms_sender_number"
              render={({ field }) => {
                const dialpadNumbers = dialpadNumbersData?.numbers || [];
                const isManualValue =
                  !!field.value &&
                  !dialpadNumbers.some(
                    (n) => n.phone_number === field.value,
                  );
                const noneOption = {
                  id: '__none__',
                  display_name: 'None',
                  source_name: null as string | null,
                };
                const options = [
                  noneOption,
                  ...dialpadNumbers.map((n) => ({
                    id: n.phone_number,
                    display_name: n.display_name,
                    source_name: n.source_name,
                  })),
                ];
                const selected = isManualValue
                  ? noneOption
                  : options.find(
                      (o) => o.id === (field.value || '__none__'),
                    ) || noneOption;

                return (
                  <FormItem>
                    <FormControl>
                      <div className="space-y-2">
                        {/* Dialpad dropdown */}
                        <GroupedCombobox
                          label={null as unknown as string}
                          groups={[
                            { label: 'SMS Sender Numbers', options },
                          ]}
                          value={selected}
                          onChange={(val) =>
                            field.onChange(
                              !val || val.id === '__none__'
                                ? ''
                                : val.id,
                            )
                          }
                          placeholder={
                            isLoadingDialpadNumbers
                              ? 'Loading numbers...'
                              : mode === 'create'
                                ? 'Save firm first to select SMS sender'
                                : 'Select an SMS Sender Number'
                          }
                          emptyText="No SMS Sender Numbers found"
                          displayValue={(item) =>
                            `${(item as any).display_name || ''} ${item.id}`
                          }
                          renderItem={({ item }) => (
                            <div className="flex flex-col">
                              <div className="text-sm">
                                {item.id === '__none__'
                                  ? 'None'
                                  : (item as any).display_name}
                              </div>
                              {item.id !== '__none__' &&
                              (item as any).source_name ? (
                                <div className="text-xs text-zinc-500">
                                  {(item as any).source_name}
                                </div>
                              ) : null}
                            </div>
                          )}
                          renderSelection={({ item }) => (
                            <div className="flex items-center gap-2">
                              <span>
                                {item.id === '__none__'
                                  ? 'None'
                                  : (item as any).display_name}
                              </span>
                            </div>
                          )}
                        />

                        {/* Manual override input */}
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-gray-500 whitespace-nowrap">
                            or enter manually:
                          </span>
                          <Input
                            type="tel"
                            placeholder="+1234567890"
                            value={isManualValue ? (field.value ?? '') : ''}
                            onChange={(e) => field.onChange(e.target.value || '')}
                          />
                        </div>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
          </FirmFieldRow>

          {/* Address Section */}
          <div className="py-4 border-t">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">
              Firm Address
            </h3>
            <div className="space-y-4">
              <FirmFieldRow label="Address Line 1">
                <FormField
                  control={form.control}
                  name="address.line1"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          placeholder="Street address"
                          {...field}
                          value={field.value ?? ''}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </FirmFieldRow>
              <FirmFieldRow label="Address Line 2">
                <FormField
                  control={form.control}
                  name="address.line2"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          placeholder="Suite, unit, building, floor, etc."
                          {...field}
                          value={field.value ?? ''}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </FirmFieldRow>
              <FirmFieldRow label="City">
                <FormField
                  control={form.control}
                  name="address.city"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          placeholder="City"
                          {...field}
                          value={field.value ?? ''}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </FirmFieldRow>
              <FirmFieldRow label="State">
                <FormField
                  control={form.control}
                  name="address.state"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          placeholder="State / Province"
                          {...field}
                          value={field.value ?? ''}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </FirmFieldRow>
              <FirmFieldRow label="Postal Code">
                <FormField
                  control={form.control}
                  name="address.postcode"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          placeholder="ZIP / Postal code"
                          {...field}
                          value={field.value ?? ''}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </FirmFieldRow>
            </div>
          </div>
        </FirmLayout>
      </form>
    </Form>
  );
};
